// Rendu d'un champ du schéma de formulaire (quatorze types) avec les composants de la charte :
// libellé, aide, obligation, erreur. La valeur est typée comme l'attend le moteur partagé
// (nombre, booléen, tableau, objet adresse ou localisation, identifiant de pièce).
import type { Champ } from '@cc/contrats';
import { CaseACocher, Champ as ChampTexte, DateChamp, Selection, ZoneTexte } from '@cc/ui';
import { useId } from 'react';
import { ChampTableau } from './ChampTableau';
import styles from './formulaire.module.css';

export interface PieceDisponible {
  id: string;
  titre: string;
}

export interface ProprietesChampDynamique {
  champ: Champ;
  valeur: unknown;
  onChanger: (valeur: unknown) => void;
  erreur?: string;
  erreurs?: { champ: string; message: string }[];
  /** Pièces déjà déposées, proposées aux champs fichier. */
  pieces?: PieceDisponible[];
  disabled?: boolean;
  /** Valeur calculée par le serveur : affichée, jamais saisie. */
  valeurCalculee?: number | null;
}

const objet = (v: unknown): Record<string, unknown> => (typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : {});

function nombreDepuis(brut: string): unknown {
  if (brut.trim() === '') return undefined;
  const n = Number(brut.replace(',', '.'));
  return Number.isNaN(n) ? brut : n;
}

function Groupe({ champ, erreur, children, id }: { champ: Champ; erreur?: string; children: React.ReactNode; id: string }) {
  return (
    <fieldset className={styles.groupe} aria-describedby={erreur ? `${id}-erreur` : undefined} aria-invalid={erreur ? true : undefined}>
      <legend>
        {champ.libelle}
        {champ.obligatoire ? <span aria-hidden> *</span> : null}
      </legend>
      {champ.aide ? <p className={styles.aide}>{champ.aide}</p> : null}
      {children}
      {erreur ? (
        <p id={`${id}-erreur`} className={styles.erreur} role="alert">
          {erreur}
        </p>
      ) : null}
    </fieldset>
  );
}

export function ChampDynamique({ champ, valeur, onChanger, erreur, erreurs = [], pieces = [], disabled, valeurCalculee }: ProprietesChampDynamique) {
  const id = `champ-${useId()}-${champ.code}`;
  const commun = { id, libelle: champ.libelle, aide: champ.aide, erreur, required: champ.obligatoire, facultatif: !champ.obligatoire, disabled };

  if (champ.calcul) {
    return (
      <div className={styles.calcule}>
        <span className={styles.libelleCalcule}>{champ.libelle}</span>
        <output className="cc-num" aria-label={champ.libelle}>
          {valeurCalculee === null || valeurCalculee === undefined ? 'calculé au dépôt' : `${valeurCalculee.toLocaleString('fr-FR')}${champ.type === 'montant' ? ' francs CFA' : ''}`}
        </output>
        <span className={styles.aide}>{champ.aide ?? 'Valeur calculée par le serveur à partir des autres champs.'}</span>
      </div>
    );
  }

  switch (champ.type) {
    case 'texte_court':
      return <ChampTexte {...commun} value={typeof valeur === 'string' ? valeur : ''} maxLength={champ.validation?.longueur_max ?? 255} onChange={(e) => onChanger(e.target.value)} />;
    case 'texte_long':
      return <ZoneTexte {...commun} value={typeof valeur === 'string' ? valeur : ''} rows={4} maxLength={champ.validation?.longueur_max ?? 5000} onChange={(e) => onChanger(e.target.value)} />;
    case 'nombre':
    case 'montant':
      return (
        <ChampTexte
          {...commun}
          type="number"
          inputMode="decimal"
          step={champ.type === 'montant' ? 1 : 'any'}
          min={champ.validation?.min ?? (champ.type === 'montant' ? 0 : undefined)}
          max={champ.validation?.max}
          aide={champ.aide ?? (champ.type === 'montant' ? 'En francs CFA, sans décimale' : undefined)}
          value={typeof valeur === 'number' ? String(valeur) : typeof valeur === 'string' ? valeur : ''}
          onChange={(e) => onChanger(nombreDepuis(e.target.value))}
        />
      );
    case 'date':
      return <DateChamp {...commun} min={champ.validation?.date_min} max={champ.validation?.date_max} value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => onChanger(e.target.value || undefined)} />;
    case 'telephone':
      return <ChampTexte {...commun} type="tel" autoComplete="tel" value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => onChanger(e.target.value)} />;
    case 'courriel':
      return <ChampTexte {...commun} type="email" autoComplete="email" value={typeof valeur === 'string' ? valeur : ''} onChange={(e) => onChanger(e.target.value)} />;
    case 'choix_unique':
      return (
        <Selection
          {...commun}
          optionVide="Choisir"
          value={typeof valeur === 'string' ? valeur : ''}
          options={(champ.options ?? []).map((o) => ({ valeur: o.valeur, libelle: o.libelle }))}
          onChange={(e) => onChanger(e.target.value || undefined)}
        />
      );
    case 'choix_multiple': {
      const choisis = Array.isArray(valeur) ? (valeur as string[]) : [];
      return (
        <Groupe champ={champ} erreur={erreur} id={id}>
          <div className={styles.cases}>
            {(champ.options ?? []).map((o) => (
              <CaseACocher
                key={o.valeur}
                libelle={o.libelle}
                disabled={disabled}
                checked={choisis.includes(o.valeur)}
                onChange={(e) => onChanger(e.target.checked ? [...choisis, o.valeur] : choisis.filter((x) => x !== o.valeur))}
              />
            ))}
          </div>
        </Groupe>
      );
    }
    case 'oui_non':
      return (
        <Groupe champ={champ} erreur={erreur} id={id}>
          <div className={styles.ouiNon} role="radiogroup" aria-label={champ.libelle}>
            {[
              { v: true, l: 'Oui' },
              { v: false, l: 'Non' },
            ].map((o) => (
              <label key={o.l} className={styles.radio} data-choisi={valeur === o.v || undefined}>
                <input type="radio" name={id} disabled={disabled} checked={valeur === o.v} onChange={() => onChanger(o.v)} />
                <span>{o.l}</span>
              </label>
            ))}
          </div>
        </Groupe>
      );
    case 'adresse': {
      const a = objet(valeur);
      const maj = (cle: string, v: string) => onChanger({ ...a, [cle]: v || undefined });
      return (
        <Groupe champ={champ} erreur={erreur} id={id}>
          <div className={styles.grille}>
            <ChampTexte libelle="Voie" required value={(a.voie as string) ?? ''} disabled={disabled} onChange={(e) => maj('voie', e.target.value)} erreur={erreurs.find((e) => e.champ === `${champ.code}.voie`)?.message} />
            <ChampTexte libelle="Quartier" facultatif value={(a.quartier as string) ?? ''} disabled={disabled} onChange={(e) => maj('quartier', e.target.value)} />
            <ChampTexte libelle="Ville" facultatif value={(a.ville as string) ?? ''} disabled={disabled} onChange={(e) => maj('ville', e.target.value)} />
            <ChampTexte libelle="Complément" facultatif value={(a.complement as string) ?? ''} disabled={disabled} onChange={(e) => maj('complement', e.target.value)} />
          </div>
        </Groupe>
      );
    }
    case 'localisation': {
      const p = objet(valeur);
      const maj = (cle: 'latitude' | 'longitude', brut: string) => {
        const n = nombreDepuis(brut);
        const suivant = { ...p, [cle]: n };
        onChanger(suivant.latitude === undefined && suivant.longitude === undefined ? undefined : suivant);
      };
      return (
        <Groupe champ={champ} erreur={erreur} id={id}>
          <p className={styles.aide}>Coordonnées en degrés décimaux. Le pointage sur la carte est livré avec les composants cartographiques.</p>
          <div className={styles.grille}>
            <ChampTexte libelle="Latitude" type="number" step="any" min={-90} max={90} value={p.latitude === undefined ? '' : String(p.latitude)} disabled={disabled} onChange={(e) => maj('latitude', e.target.value)} />
            <ChampTexte libelle="Longitude" type="number" step="any" min={-180} max={180} value={p.longitude === undefined ? '' : String(p.longitude)} disabled={disabled} onChange={(e) => maj('longitude', e.target.value)} />
          </div>
        </Groupe>
      );
    }
    case 'fichier':
      return (
        <Selection
          {...commun}
          aide={champ.aide ?? (pieces.length === 0 ? 'Aucune pièce déposée pour l\'instant : déposez-la à l\'étape des pièces, elle apparaîtra ici.' : 'Pièce déposée à rattacher à ce champ.')}
          optionVide={pieces.length === 0 ? 'Aucune pièce disponible' : 'Choisir une pièce déposée'}
          value={typeof valeur === 'string' ? valeur : ''}
          options={pieces.map((p) => ({ valeur: p.id, libelle: p.titre }))}
          onChange={(e) => onChanger(e.target.value || undefined)}
        />
      );
    case 'tableau':
      return (
        <Groupe champ={champ} erreur={erreur} id={id}>
          <ChampTableau champ={champ} lignes={Array.isArray(valeur) ? (valeur as Record<string, unknown>[]) : []} onChanger={(l) => onChanger(l.length === 0 ? undefined : l)} erreurs={erreurs} disabled={disabled} />
        </Groupe>
      );
  }
}
